import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button, Card, Spinner, Table, Td, Th } from '@/components/admin/ui';
import {
  BarList,
  ChartCard,
  DeviceLink,
  EmptyState,
  KpiTile,
} from '@/components';
import type { GameDetail, GamesTrendResponse } from '@/lib/admin-types';
import { PRIMARY_HEX } from '@/lib/colors';
import {
  formatAdminDate,
  formatAdminDateTime,
  formatDurationSeconds,
  formatInt,
  formatNumber,
} from '@/lib/format';
import { apiJson, ApiError } from '@/lib/api';

type Row = {
  run_id: number;
  installation_id: string;
  nickname: string | null;
  score: number;
  duration_seconds: number | null;
  played_at: string;
};

function RunsTable({
  rows,
  onDelete,
}: {
  rows: Row[];
  onDelete: (runId: number) => void;
}) {
  return (
    <Card>
      <div className="max-w-full overflow-x-auto">
        <Table>
          <thead>
            <tr>
              <Th>Score</Th>
              <Th>Nickname</Th>
              <Th>Installation</Th>
              <Th>Duration</Th>
              <Th>When</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.run_id}>
                <Td>{formatInt(r.score)}</Td>
                <Td>
                  {r.nickname ? (
                    <DeviceLink
                      installationId={r.installation_id}
                      nickname={r.nickname}
                      label="nickname"
                    />
                  ) : (
                    '—'
                  )}
                </Td>
                <Td className="max-w-[10rem] truncate">
                  <DeviceLink installationId={r.installation_id} label="id" />
                </Td>
                <Td className="whitespace-nowrap text-xs text-nova-muted-foreground">
                  {formatDurationSeconds(r.duration_seconds)}
                </Td>
                <Td className="whitespace-nowrap text-xs text-nova-muted-foreground">
                  {formatAdminDateTime(r.played_at)}
                </Td>
                <Td>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      if (confirm('Delete this run?')) {
                        onDelete(r.run_id);
                      }
                    }}
                  >
                    Delete
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </Card>
  );
}

function RunsSection({
  title,
  loading,
  error,
  empty,
  rows,
  hasRuns,
  onRetry,
  onDelete,
}: {
  title: string;
  loading: boolean;
  error: Error | null;
  empty: string;
  rows: Row[];
  hasRuns: boolean;
  onRetry: () => void;
  onDelete: (runId: number) => void;
}) {
  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold text-nova-foreground">{title}</h2>
      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">
          {error.message}
          <button type="button" className="ml-2 underline" onClick={onRetry}>
            Retry
          </button>
        </p>
      ) : !hasRuns || rows.length === 0 ? (
        <Card padding="md">
          <EmptyState title="No runs yet" hint={empty} />
        </Card>
      ) : (
        <RunsTable
          rows={rows}
          onDelete={(runId) => {
            onDelete(runId);
          }}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/games/$slug')({ component: GameDetailPage });

function fmtDay(iso: string) {
  const [, m, day] = iso.split('-');
  return `${m}/${day}`;
}

function chartTooltipFormatter(value: unknown, name: unknown) {
  const v = typeof value === 'number' ? formatInt(value) : String(value);
  return [v, String(name)] as [string, string];
}

function GameDetailPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const detailQ = useQuery({
    queryKey: ['admin', 'game-detail', slug],
    queryFn: () =>
      apiJson<GameDetail>(`/v1/admin/games/${encodeURIComponent(slug)}`),
  });

  const trendQ = useQuery({
    queryKey: ['admin', 'games-trend', '30d', slug],
    queryFn: () =>
      apiJson<GamesTrendResponse>('/v1/admin/analytics/games-trend?range=30d'),
    enabled: detailQ.isSuccess,
  });

  const lbQ = useQuery({
    queryKey: ['admin', 'leaderboard', slug],
    queryFn: () =>
      apiJson<Row[]>(`/v1/admin/games/${encodeURIComponent(slug)}/leaderboard?limit=100`),
    enabled: detailQ.isSuccess,
  });

  const runsQ = useQuery({
    queryKey: ['admin', 'game-runs', slug],
    queryFn: () =>
      apiJson<Row[]>(`/v1/admin/games/${encodeURIComponent(slug)}/runs?limit=100`),
    enabled: detailQ.isSuccess,
  });

  const del = useMutation({
    mutationFn: (runId: number) =>
      apiJson<void>(`/v1/admin/games/runs/${runId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'game-detail', slug] });
      void qc.invalidateQueries({ queryKey: ['admin', 'leaderboard', slug] });
      void qc.invalidateQueries({ queryKey: ['admin', 'game-runs', slug] });
      void qc.invalidateQueries({ queryKey: ['admin', 'games-trend'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'games'] });
    },
  });

  if (detailQ.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (detailQ.isError) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-600">
          {(detailQ.error as Error).message}
          <button type="button" className="ml-2 underline" onClick={() => void detailQ.refetch()}>
            Retry
          </button>
        </p>
        <Button variant="secondary" onClick={() => void navigate({ to: '/admin/games' })}>
          All games
        </Button>
      </div>
    );
  }

  const d = detailQ.data;
  if (!d) {
    return null;
  }
  const trendPoints =
    trendQ.data?.games.find((g) => g.slug === slug)?.points.map((p) => ({
      ...p,
      label: fmtDay(p.date),
    })) ?? [];

  const hasRuns = d.total_runs > 0;
  const lbRows = lbQ.data ?? [];
  const runsRows = runsQ.data ?? [];

  const handleDelete = (runId: number) => {
    void del.mutate(runId, {
      onError: (e) => {
        if (e instanceof ApiError) {
          alert(e.detail ?? e.message);
        }
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-nova-foreground">{d.display_name}</h1>
          <p className="mt-0.5 font-mono text-sm text-nova-muted-foreground">{d.slug}</p>
          <p className="mt-2 text-xs text-nova-muted-foreground">
            Registered {formatAdminDate(d.created_at)} · First run {formatAdminDateTime(d.first_run_at)} · Last run{' '}
            {formatAdminDateTime(d.last_run_at)}
          </p>
        </div>
        <Button variant="secondary" onClick={() => void navigate({ to: '/admin/games' })}>
          All games
        </Button>
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold text-nova-foreground">Summary</h2>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <KpiTile
            label="Total runs"
            value={d.total_runs}
            tooltip="All-time completed runs stored for this game."
          />
          <KpiTile
            label="Unique players"
            value={d.unique_players}
            tooltip="Distinct devices that have at least one run for this game."
          />
          <KpiTile
            label="Runs (7d)"
            value={d.runs_7d}
            tooltip="Runs with played_at in the last 7 Pacific calendar days (including today)."
          />
          <KpiTile
            label="Runs (30d)"
            value={d.runs_30d}
            tooltip="Runs with played_at in the last 30 Pacific calendar days (including today)."
          />
          <KpiTile
            label="Avg score"
            value={hasRuns ? formatNumber(d.avg_score, { maxFractionDigits: 1 }) : '—'}
            tooltip="Mean score across all runs for this game."
          />
          <KpiTile
            label="High score"
            value={d.max_score != null ? d.max_score : '—'}
            tooltip="Highest score recorded in any single run."
          />
          <KpiTile
            label="Avg session length"
            value={formatDurationSeconds(d.avg_duration_seconds)}
            tooltip="Mean duration_seconds across runs that reported a duration."
          />
        </div>
      </div>

      <Card padding="md">
        <h2 className="mb-3 text-sm font-semibold text-nova-foreground">Runs by app version</h2>
        <p className="mb-4 text-xs text-nova-muted-foreground">
          Client-reported app_version on each run. Long tails are grouped into Other.
        </p>
        <BarList data={d.by_app_version} emptyLabel="No version data on runs yet" />
      </Card>

      <ChartCard title="Runs per day (30d)" loading={trendQ.isLoading}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={trendPoints} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis
              allowDecimals={false}
              width={44}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => formatInt(Number(v))}
            />
            <Tooltip formatter={chartTooltipFormatter} />
            <Area
              type="monotone"
              dataKey="runs"
              name="Runs"
              stroke={PRIMARY_HEX}
              fill={PRIMARY_HEX}
              fillOpacity={0.2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <RunsSection
        title="Latest runs"
        loading={runsQ.isLoading}
        error={runsQ.isError ? (runsQ.error as Error) : null}
        empty="Recent sessions will appear here once players finish games."
        rows={runsRows}
        hasRuns={hasRuns}
        onRetry={() => void runsQ.refetch()}
        onDelete={handleDelete}
      />

      <RunsSection
        title="Top runs (leaderboard)"
        loading={lbQ.isLoading}
        error={lbQ.isError ? (lbQ.error as Error) : null}
        empty="Scores will appear here once players finish sessions."
        rows={lbRows}
        hasRuns={hasRuns}
        onRetry={() => void lbQ.refetch()}
        onDelete={handleDelete}
      />
    </div>
  );
}
